import { cn } from '@/lib/utils';
import { MessageContext } from 'contexts/MessageContext';
import { Pencil } from 'lucide-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
    IMessageElement,
    IStep,
    messagesState,
    sessionIdState,
    sideViewState,
    useChatInteract
} from '@chainlit/react-client';

import AutoResizeTextarea from '@/components/AutoResizeTextarea';
import { FileReferenceText } from '@/components/chat/FileReferenceChip';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Translator } from 'components/i18n';

import { DashboardReferenceChip } from '@/components/chat/DashboardReferenceChip';
import { splitReferences } from '@/lib/dashboardReferences';

import { InlinedElements } from './Content/InlinedElements';

// Helper function to check if file needs Office preview
const isOfficeFile = (fileName?: string, mimeType?: string): boolean => {
    // Check by extension
    if (fileName) {
        const ext = fileName.toLowerCase().split('.').pop() || '';
        if (
            [
                'xlsx',
                'xls',
                'xlsm',
                'xlsb',
                'docx',
                'doc',
                'pptx',
                'ppt'
            ].includes(ext)
        ) {
            return true;
        }
    }
    // Check by mime type
    if (mimeType) {
        const officeMimeTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
            'application/vnd.ms-excel', // xls
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
            'application/msword', // doc
            'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
            'application/vnd.ms-powerpoint' // ppt
        ];
        if (officeMimeTypes.includes(mimeType)) {
            return true;
        }
    }
    return false;
};

interface Props {
    message: IStep;
    elements: IMessageElement[];
}

export default function UserMessage({
    message,
    elements,
    children
}: React.PropsWithChildren<Props>) {
    const { askUser, loading, editable } = useContext(MessageContext);
    const { editMessage } = useChatInteract();
    const setMessages = useSetRecoilState(messagesState);
    const sessionId = useRecoilValue(sessionIdState);
    const disabled = loading || !!askUser;
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');

    // Modal state for file preview
    const [modalFile, setModalFile] = useState<{
        name: string;
        url: string;
        originalUrl?: string; // Store original URL for download
        type: string;
    } | null>(null);

    // Loading state for modal content
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    // Get sideView state like RightSidebar does
    const sideView = useRecoilValue(sideViewState);
    const sideViewRef = useRef(sideView);

    const messageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        sideViewRef.current = sideView;
    }, [sideView]);

    // Helper function to find blob storage URL from sideView (same source as RightSidebar)
    const findBlobStorageUrl = (fileName: string): string | null => {
        // Use sideView.elements like RightSidebar does, not the local elements prop
        const element = sideView?.elements?.find(
            (el) =>
                el.name === fileName &&
                el.url &&
                el.url.includes('.blob.core.windows.net')
        );

        if (element && element.url) {
            return element.url;
        }

        return null;
    };

    // Helper function to get blob storage URL via fetch if needed
    const fetchBlobStorageUrl = async (
        fileEndpointUrl: string
    ): Promise<string> => {
        try {
            // Try HEAD request first to check for redirect
            let response = await fetch(fileEndpointUrl, {
                method: 'HEAD',
                redirect: 'manual'
            });

            // Check for redirect in HEAD response
            if (response.status === 302 || response.status === 301) {
                const location = response.headers.get('Location');
                if (location && location.includes('.blob.core.windows.net')) {
                    return location;
                }
            }

            // If no redirect with HEAD, try GET request
            response = await fetch(fileEndpointUrl, {
                method: 'GET'
            });

            if (response.ok) {
                // Check if the final response URL is blob storage (after redirects)
                if (
                    response.url &&
                    response.url.includes('.blob.core.windows.net')
                ) {
                    return response.url;
                }

                // Try to parse response body for blob URL (in case server returns JSON)
                try {
                    const data = await response.json();
                    if (
                        data.url &&
                        data.url.includes('.blob.core.windows.net')
                    ) {
                        return data.url;
                    }
                } catch {
                    // Not JSON, ignore
                }
            }

            return fileEndpointUrl;
        } catch (error) {
            return fileEndpointUrl;
        }
    };

    // Helper function to check if URL is from blob storage (not local)
    const isBlobStorageUrl = (url?: string): boolean => {
        if (!url) return false;
        // Check for Azure Blob Storage URLs
        return url.includes('.blob.core.windows.net');
    };

    // Helper function to wait for blob storage URL to become available
    const waitForBlobStorageUrl = async (
        fileName: string
    ): Promise<string | null> => {
        const maxAttempts = 20; // Max 10 seconds (20 * 500ms)
        let attempts = 0;

        return new Promise((resolve) => {
            const poll = () => {
                const element = sideViewRef.current?.elements?.find(
                    (el) =>
                        el.name === fileName &&
                        el.url &&
                        el.url.includes('.blob.core.windows.net')
                );

                if (element && element.url) {
                    resolve(element.url);
                    return;
                }

                attempts++;
                if (attempts >= maxAttempts) {
                    resolve(null);
                    return;
                }

                setTimeout(poll, 500);
            };

            poll();
        });
    };

    // Helper function to open file modal like RightSidebar does
    const openFileModal = async (
        fileName: string,
        fileUrl: string,
        fileMime?: string
    ) => {
        setModalError(null);

        // Check if it's an Office file
        if (isOfficeFile(fileName, fileMime)) {
            if (isBlobStorageUrl(fileUrl)) {
                // Already have blob storage URL, use Office Online viewer directly
                const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    fileUrl
                )}`;

                setModalFile({
                    name: fileName,
                    url: officeUrl,
                    originalUrl: fileUrl,
                    type: 'file'
                });
            } else {
                // localhost URL - open modal with loader and wait for blob URL
                const urlWithSession = fileUrl.includes('session_id')
                    ? fileUrl
                    : `${fileUrl}${
                          fileUrl.includes('?') ? '&' : '?'
                      }session_id=${sessionId}`;

                setModalFile({
                    name: fileName,
                    url: '', // Empty URL shows loader
                    originalUrl: urlWithSession,
                    type: 'file'
                });
                setIsModalLoading(true);

                // 1) Try existing sideView URL first
                let blobUrl = findBlobStorageUrl(fileName);

                // 2) Try resolving via endpoint redirect/fetch (same idea as sidebar direct URL handling)
                if (!blobUrl) {
                    const fetchedUrl = await fetchBlobStorageUrl(
                        urlWithSession
                    );
                    if (isBlobStorageUrl(fetchedUrl)) {
                        blobUrl = fetchedUrl;
                    }
                }

                // 3) Fallback: wait briefly for sideView to receive blob URL
                if (!blobUrl) {
                    blobUrl = await waitForBlobStorageUrl(fileName);
                }

                if (blobUrl) {
                    // Got blob URL, switch to Office Online viewer
                    const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                        blobUrl
                    )}`;

                    setModalFile((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  url: officeUrl,
                                  originalUrl: blobUrl
                              }
                            : null
                    );
                } else {
                    setModalError(
                        'Forhåndsvisning kunne ikke indlæses. Du kan downloade filen manuelt.'
                    );
                }

                setIsModalLoading(false);
            }
        } else {
            // For non-Office files, open in modal
            // Add session_id like RightSidebar does
            const urlWithSession = fileUrl.includes('session_id')
                ? fileUrl
                : `${fileUrl}${
                      fileUrl.includes('?') ? '&' : '?'
                  }session_id=${sessionId}`;

            setModalFile({
                name: fileName,
                url: urlWithSession,
                originalUrl: urlWithSession,
                type: 'file'
            });
        }
    };

    // Handle clicks on file/PDF/image elements to open modal
    useEffect(() => {
        const handleElementClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const fileLink = target.closest('a[href]') as HTMLAnchorElement;

            // First try to find a file link
            if (
                fileLink &&
                (fileLink.classList.contains('inline-file') ||
                    fileLink.classList.contains('inline-pdf') ||
                    fileLink.classList.contains('inline-image') ||
                    fileLink.classList.contains('inline-text') ||
                    fileLink.href) // Catch any link that might be a file
            ) {
                event.preventDefault();
                const href = fileLink.href;
                const dataFileUrl = fileLink.getAttribute('data-file-url');
                const dataFileName = fileLink.getAttribute('data-file-name');
                const dataFileMime = fileLink.getAttribute('data-file-mime');
                const fileName =
                    dataFileName || href.split('/').pop() || 'file';
                const fileUrl = dataFileUrl || href;

                // Use the new openFileModal function
                openFileModal(fileName, fileUrl, dataFileMime || undefined);
                return;
            }

            // Also handle clicks on attachment cards that might not be wrapped in links
            const attachmentCard = target.closest(
                '[data-file-url]'
            ) as HTMLElement;
            if (attachmentCard) {
                event.preventDefault();
                const fileUrl = attachmentCard.getAttribute('data-file-url');
                const fileName =
                    attachmentCard.getAttribute('data-file-name') || 'file';
                const fileMime = attachmentCard.getAttribute('data-file-mime');

                if (fileUrl) {
                    // Use the new openFileModal function with mime type
                    openFileModal(fileName, fileUrl, fileMime || undefined);
                }
            }
        };

        const messageElement = messageRef.current;
        if (messageElement) {
            messageElement.addEventListener('click', handleElementClick);
            return () => {
                messageElement.removeEventListener('click', handleElementClick);
            };
        }
    }, [sessionId]);

    const inlineElements = useMemo(() => {
        return elements.filter(
            (el) => el.forId === message.id && el.display === 'inline'
        );
    }, [message.id, elements]);

    const formattedMessageDate = useMemo(() => {
        if (!message.createdAt) {
            return null;
        }

        const date = new Date(message.createdAt);
        if (Number.isNaN(date.getTime())) {
            return null;
        }

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    }, [message.createdAt]);

    const references = useMemo(
        () => splitReferences(message.output),
        [message.output]
    );

    const handleEdit = () => {
        if (editValue) {
            setMessages((prev) => {
                const index = prev.findIndex((m) => m.id === message.id);
                if (index === -1) {
                    return prev;
                }
                const slice = prev.slice(0, index + 1);
                slice[index].steps = [];
                return slice;
            });
            setIsEditing(false);
            editMessage({
                ...message,
                output: references.block + editValue
            });
        }
    };

    return (
        <div className="flex flex-col gap-1 w-full" ref={messageRef}>
            {!isEditing && (
                <InlinedElements
                    elements={inlineElements}
                    compactFiles
                    className={
                        inlineElements.length > 1
                            ? 'flex-col items-end'
                            : 'items-end'
                    }
                />
            )}

            <div className="group flex flex-col items-end gap-1 pt-1 w-full">
                {references.chips.length > 0 && !isEditing && (
                    <div className="flex flex-wrap justify-end gap-2">
                        {references.chips.map((chip, index) => (
                            <DashboardReferenceChip key={index} chip={chip} />
                        ))}
                    </div>
                )}
                {(references.text.length !== 0 || isEditing) && (
                    <>
                        <div
                            className={cn(
                                'relative bg-accent px-5 py-2.5 rounded-3xl',
                                inlineElements.length ? 'rounded-tr-lg' : '',
                                isEditing
                                    ? 'w-full grow'
                                    : 'max-w-[70%] grow-0',
                                editable ? '' : 'ml-auto'
                            )}
                        >
                            {isEditing ? (
                                <div className="flex flex-col justify-start bg-accent w-full">
                                    <InlinedElements
                                        elements={inlineElements}
                                        compactFiles
                                        className={
                                            inlineElements.length > 1
                                                ? 'flex-col items-start'
                                                : 'items-start'
                                        }
                                    />
                                    <AutoResizeTextarea
                                        id="edit-chat-input"
                                        autoFocus
                                        value={editValue}
                                        onChange={(e) =>
                                            setEditValue(e.target.value)
                                        }
                                        className="bg-transparent mt-1 pt-2 placeholder:font-medium text-base placeholder:text-base"
                                        maxHeight={250}
                                    />

                                    <div className="flex justify-end gap-4 pt-2">
                                        <Button
                                            variant="ghost"
                                            onClick={() => setIsEditing(false)}
                                        >
                                            <Translator path="common.actions.cancel" />
                                        </Button>
                                        <Button
                                            className="confirm-edit"
                                            disabled={disabled}
                                            onClick={handleEdit}
                                        >
                                            <Translator path="common.actions.confirm" />
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col">
                                    {message.command ? (
                                        <div className="font-bold text-[#08f] command-span">
                                            {message.command}
                                        </div>
                                    ) : null}
                                    <FileReferenceText text={references.text} />
                                </div>
                            )}
                        </div>
                    </>
                )}

                {!isEditing && editable && !disabled && (
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 ml-auto transition-opacity">
                        {formattedMessageDate ? (
                            <span className="text-muted-foreground text-sm whitespace-nowrap">
                                {formattedMessageDate}
                            </span>
                        ) : null}
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            className="edit-message"
                            onClick={() => {
                                setEditValue(references.text);
                                setIsEditing(true);
                            }}
                            disabled={disabled}
                        >
                            <Pencil strokeWidth={1.5} />
                        </Button>
                    </div>
                )}
            </div>

            {/* File Preview Modal */}
            <Dialog
                open={!!modalFile}
                onOpenChange={(open) => {
                    if (!open) {
                        setModalFile(null);
                        setModalError(null);
                        setIsModalLoading(false);
                    }
                }}
            >
                <DialogContent className="flex flex-col w-full max-w-[90vw] h-full max-h-[90vh]">
                    <DialogHeader className="shrink-0 pb-2">
                        <DialogTitle className="pr-20">
                            <span>{modalFile?.name}</span>
                        </DialogTitle>
                        {/* Download button positioned to the left of the close button */}
                        {modalFile &&
                            !modalFile.name.toLowerCase().endsWith('.pdf') && (
                                <Button
                                    variant="tertiary"
                                    size="compact"
                                    className="top-3.5 right-12 absolute"
                                    onClick={() =>
                                        (modalFile?.originalUrl ||
                                            modalFile?.url) &&
                                        window.open(
                                            modalFile.originalUrl ||
                                                modalFile.url,
                                            '_blank'
                                        )
                                    }
                                >
                                    Download
                                </Button>
                            )}
                    </DialogHeader>
                    <div className="flex-1 min-h-0 overflow-hidden">
                        {modalFile && (
                            <>
                                {isModalLoading || !modalFile.url ? (
                                    <div className="flex flex-col justify-center items-center h-full">
                                        {modalError ? (
                                            <>
                                                <p className="max-w-md text-muted-foreground text-sm text-center">
                                                    {modalError}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="mb-3 border-foreground border-b-2 rounded-full w-8 h-8 animate-spin"></div>
                                                <p className="text-muted-foreground text-sm">
                                                    Loading file...
                                                </p>
                                                <p className="mt-1 text-muted-foreground text-xs">
                                                    Finding blob storage URL
                                                </p>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <iframe
                                        src={modalFile.url}
                                        className="border-0 rounded-md w-full h-full"
                                        title={modalFile.name}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
